/**
 * batch-65 uitrusting-flow (FO-480) — wanneer kan een medewerker zijn
 * uitrusting ophalen (of krijgt hij hem mee)?
 *
 * Pure afleiding op planning- en verlofinvoer die de API per medewerker
 * batch-laadt: geen DB, geen klok (de caller geeft `klaarVanaf` mee). Zo rekenen
 * API, tests en een eventuele herberekening exact hetzelfde.
 *
 * Per werkdag vanaf `klaarVanaf` (weekend en verplichte feestdagen tellen niet,
 * het magazijn is dan dicht) binnen een horizon van werkdagen:
 *  - afwezig (verlof, ziek) -> overslaan (spec §5.2);
 *  - een interne kantoorwerkbon -> KANTOORDAG: de medewerker is er toch;
 *  - geen enkele dienst -> VRIJE_DAG;
 *  - alleen klantdiensten -> een vrij moment VOOR_DIENST (het uur vóór de eerste
 *    dienst, binnen de openingstijden) of anders NA_DIENST; past geen van
 *    beide, dan geen kandidaat.
 *
 * Kiezen: score = werkdag-index + straf per soort (kantoordag 0, vrije dag 1,
 * voor/na een dienst 3). Een kantoordag morgen wint dus van een vrije dag
 * vandaag + 2, maar een vrije dag morgen wint van een kantoordag over twee
 * weken. Gelijke score: de lagere straf, dan de vroegste dag. De alternatieven
 * zijn de volgende beste kandidaten op een andere dag, in datumvolgorde.
 */

import type { LeverMomentDTO, LeverMomentSoort } from '@streep/types'

export interface LeverDienstInput {
  /** "HH:mm" (NL-tijd) of null wanneer onbekend. */
  start: string | null
  eind: string | null
  /** Interne kantoorwerkbon (werkbontype KANTOOR): de medewerker is die dag op kantoor. */
  kantoor: boolean
  /** Voor de uitleg ("vóór je dienst bij ..."). */
  klantNaam?: string | null
}

export interface LeverDagInput {
  /** yyyy-mm-dd (NL-kalenderdag). */
  datum: string
  /** Verlof (aangevraagd of goedgekeurd), ziek of anders afwezig: overslaan. */
  afwezig: boolean
  diensten: readonly LeverDienstInput[]
}

export interface LeverVoorstelInput {
  /** yyyy-mm-dd: de eerste dag waarop de spullen klaar kunnen liggen. */
  klaarVanaf: string
  /** Planning per dag; een ontbrekende dag telt als vrij (geen dienst, niet afwezig). */
  dagen: readonly LeverDagInput[]
  /** yyyy-mm-dd, verplichte feestdagen (magazijn dicht). */
  feestdagen?: readonly string[]
  /** Hoeveel werkdagen vooruit gekeken wordt. */
  horizonWerkdagen?: number
  /** Openingstijden van het magazijn ("HH:mm"). */
  venster?: { van: string; tot: string }
  /** Maximaal aantal alternatieven naast het voorstel. */
  maxAlternatieven?: number
  /** Dagen die niet meer voorgesteld worden (bv. de net gemiste afspraak). */
  uitsluiten?: readonly string[]
}

export interface LeverVoorstelUitkomst {
  voorstel: LeverMomentDTO | null
  alternatieven: LeverMomentDTO[]
}

/** Standaard openingstijden van het magazijn. */
export const LEVER_STANDAARD_VENSTER = { van: '07:30', tot: '16:30' } as const
/** Standaard horizon: twee werkweken. */
export const LEVER_HORIZON_WERKDAGEN = 10
/** Standaard aantal alternatieven (spec: 2-3). */
export const LEVER_MAX_ALTERNATIEVEN = 3

/** Kortste venster dat nog als "vrij moment" telt. */
const MIN_VENSTER_MIN = 30
/** Lengte van een moment vóór of na een dienst. */
const DIENST_MARGE_MIN = 60

const STRAF: Record<LeverMomentSoort, number> = {
  KANTOORDAG: 0,
  VRIJE_DAG: 1,
  VOOR_DIENST: 3,
  NA_DIENST: 3,
  HANDMATIG: 0,
}

const DAGNAMEN = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'] as const

// ─── Datum- en tijdhulpjes (strings, geen tijdzone-afhankelijkheid) ─────────

function naarUtc(datum: string): Date {
  const [j, m, d] = datum.split('-').map(Number)
  return new Date(Date.UTC(j ?? 1970, (m ?? 1) - 1, d ?? 1))
}

function naarDatum(d: Date): string {
  return d.toISOString().slice(0, 10)
}

/** yyyy-mm-dd plus `n` kalenderdagen. */
export function leverPlusDagen(datum: string, n: number): string {
  const d = naarUtc(datum)
  d.setUTCDate(d.getUTCDate() + n)
  return naarDatum(d)
}

/** Ma-vr en geen verplichte feestdag. */
export function isLeverWerkdag(datum: string, feestdagen: readonly string[] = []): boolean {
  const dag = naarUtc(datum).getUTCDay()
  return dag !== 0 && dag !== 6 && !feestdagen.includes(datum)
}

/** De eerste werkdag STRIKT na `datum`. */
export function volgendeLeverWerkdag(datum: string, feestdagen: readonly string[] = []): string {
  let d = leverPlusDagen(datum, 1)
  for (let i = 0; i < 31 && !isLeverWerkdag(d, feestdagen); i++) d = leverPlusDagen(d, 1)
  return d
}

function minuten(tijd: string): number {
  const [u, m] = tijd.split(':').map(Number)
  return (u ?? 0) * 60 + (m ?? 0)
}

function tijd(min: number): string {
  const u = Math.floor(min / 60)
  const m = min % 60
  return `${String(u).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

/** "di 29-09 tussen 07:30 en 08:30" (of zonder tijden: "di 29-09"). */
export function leverMomentTekst(moment: {
  datum: string
  tijdVan?: string | null
  tijdTot?: string | null
}): string {
  const d = naarUtc(moment.datum)
  const dag = `${DAGNAMEN[d.getUTCDay()]} ${String(d.getUTCDate()).padStart(2, '0')}-${String(
    d.getUTCMonth() + 1,
  ).padStart(2, '0')}`
  if (moment.tijdVan && moment.tijdTot) return `${dag} tussen ${moment.tijdVan} en ${moment.tijdTot}`
  return dag
}

// ─── Kandidaten per dag ─────────────────────────────────────────────────────

interface Kandidaat {
  moment: LeverMomentDTO
  index: number
  score: number
}

function kandidaatVoorDag(
  dag: LeverDagInput,
  venster: { van: number; tot: number },
): Omit<LeverMomentDTO, 'datum'> | null {
  const kantoor = dag.diensten.find((d) => d.kantoor)
  if (kantoor) {
    // Overlap van de kantoordienst met de openingstijden; te krap of onbekend
    // -> de gewone openingstijden (hij is er die dag toch).
    let van = venster.van
    let tot = venster.tot
    if (kantoor.start && kantoor.eind) {
      const s = Math.max(venster.van, minuten(kantoor.start))
      const e = Math.min(venster.tot, minuten(kantoor.eind))
      if (e - s >= MIN_VENSTER_MIN) {
        van = s
        tot = e
      }
    }
    return { tijdVan: tijd(van), tijdTot: tijd(tot), soort: 'KANTOORDAG', reden: 'Je bent die dag op kantoor' }
  }

  if (dag.diensten.length === 0) {
    return {
      tijdVan: tijd(venster.van),
      tijdTot: tijd(venster.tot),
      soort: 'VRIJE_DAG',
      reden: 'Je hebt die dag geen werkbon ingepland',
    }
  }

  // Alleen klantdiensten: zonder tijden is er geen vrij moment te vinden.
  const metTijd = dag.diensten.filter(
    (d): d is LeverDienstInput & { start: string; eind: string } => d.start != null && d.eind != null,
  )
  if (metTijd.length !== dag.diensten.length || metTijd.length === 0) return null
  const eerste = [...metTijd].sort((a, b) => minuten(a.start) - minuten(b.start))[0]
  const laatste = [...metTijd].sort((a, b) => minuten(b.eind) - minuten(a.eind))[0]
  if (!eerste || !laatste) return null

  // Beide momenten liggen altijd BINNEN de openingstijden: bij een avond- of
  // nachtdienst eindigt het moment vóór de dienst bij sluitingstijd (niet bij de
  // start van de dienst, want dan staat de monteur voor een dichte deur), en na
  // een nachtdienst die vóór opening eindigt begint het moment bij opening.
  const start = minuten(eerste.start)
  const voorTot = Math.min(start, venster.tot)
  const voorVan = Math.max(venster.van, voorTot - DIENST_MARGE_MIN)
  if (voorTot - voorVan >= MIN_VENSTER_MIN) {
    const klant = eerste.klantNaam ? ` bij ${eerste.klantNaam}` : ''
    return {
      tijdVan: tijd(voorVan),
      tijdTot: tijd(voorTot),
      soort: 'VOOR_DIENST',
      reden: `Vóór je dienst${klant} (start ${eerste.start})`,
    }
  }
  const eind = minuten(laatste.eind)
  const naVan = Math.max(eind, venster.van)
  const naTot = Math.min(venster.tot, naVan + DIENST_MARGE_MIN)
  if (naTot - naVan >= MIN_VENSTER_MIN) {
    const klant = laatste.klantNaam ? ` bij ${laatste.klantNaam}` : ''
    return {
      tijdVan: tijd(naVan),
      tijdTot: tijd(naTot),
      soort: 'NA_DIENST',
      reden: `Na je dienst${klant} (tot ${laatste.eind})`,
    }
  }
  return null
}

/**
 * Het voorgestelde moment plus de alternatieven waaruit de medewerker mag
 * kiezen. `voorstel` is null als er binnen de horizon geen enkel moment past
 * (bv. de hele periode verlof of ziek): dan plant het magazijn handmatig.
 */
export function bepaalLeverVoorstel(input: LeverVoorstelInput): LeverVoorstelUitkomst {
  const feestdagen = input.feestdagen ?? []
  const uitsluiten = new Set(input.uitsluiten ?? [])
  const horizon = Math.max(1, input.horizonWerkdagen ?? LEVER_HORIZON_WERKDAGEN)
  const max = Math.max(0, input.maxAlternatieven ?? LEVER_MAX_ALTERNATIEVEN)
  const v = input.venster ?? LEVER_STANDAARD_VENSTER
  const venster = { van: minuten(v.van), tot: minuten(v.tot) }
  const perDatum = new Map(input.dagen.map((d) => [d.datum, d]))

  const kandidaten: Kandidaat[] = []
  let datum = input.klaarVanaf
  let index = 0
  // Veiligheidsklep: nooit meer dan horizon + ruim een maand aan kalenderdagen.
  for (let stap = 0; index < horizon && stap < horizon * 2 + 31; stap++) {
    if (isLeverWerkdag(datum, feestdagen)) {
      const dag = perDatum.get(datum) ?? { datum, afwezig: false, diensten: [] }
      if (!dag.afwezig && !uitsluiten.has(datum)) {
        const k = kandidaatVoorDag(dag, venster)
        if (k) {
          kandidaten.push({ moment: { datum, ...k }, index, score: index + STRAF[k.soort] })
        }
      }
      index += 1
    }
    datum = leverPlusDagen(datum, 1)
  }

  const gerangschikt = [...kandidaten].sort(
    (a, b) =>
      a.score - b.score ||
      STRAF[a.moment.soort] - STRAF[b.moment.soort] ||
      a.moment.datum.localeCompare(b.moment.datum),
  )
  const beste = gerangschikt[0]
  if (!beste) return { voorstel: null, alternatieven: [] }

  const alternatieven = gerangschikt
    .slice(1)
    .filter((k) => k.moment.datum !== beste.moment.datum)
    .slice(0, max)
    .map((k) => k.moment)
    .sort((a, b) => a.datum.localeCompare(b.datum))

  return { voorstel: beste.moment, alternatieven }
}
